import React, { useCallback, useEffect, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AcaoIcone } from "../../components/PadraoTela";
import { listarClientesContaCorrente } from "../../services/api-conta-corrente.service";
import TableCard from "../../components/TableCard";
import { debounce } from "lodash";
import { formatValue, formatCurrency } from "../../utils/formatValue";
import styles from "./styles.module.css";
import { useNavigate } from "react-router-dom";
import SnackBar from "../../components/SnackBar";

// Larguras fixas (como na tela de Usuários): a Razão Social trunca com "…" e tooltip, sem rolagem lateral.
const COLUNAS_LARGURA = {
  "Razão Social": "380px",
  CNPJ: "170px",
  Saldo: "170px",
  "Última Atualização": "170px",
  Status: "120px",
  Ações: "88px",
};

const ContaCorrente = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const navigate = useNavigate();

  const columns = [
    "Razão Social",
    "CNPJ",
    "Saldo",
    "Última Atualização",
    "Status",
  ];

    const formatStatus = (status) => {
      switch (status) {
        case "ativo":
          return (
            <span className={`${styles.status_container} ${styles.ativo}`}>
              <span className={styles.status_dot} /> Ativo
            </span>
          );
        case "bloqueado":
          return (
            <span className={`${styles.status_container} ${styles.bloqueado}`}>
              <span className={styles.status_dot} /> Bloqueado
            </span>
          );
        case "inativo":
          return (
            <span className={`${styles.status_container} ${styles.inativo}`}>
              <span className={styles.status_dot} /> Inativo
            </span>
          );
        default:
          return (
            <span className={`${styles.status_container} ${styles.inativo}`}>
              <span className={styles.status_dot} /> {status}
            </span>
          );
      }
    };


  const handleOrganizaDados = useCallback((dados) => {
    if (!dados || !Array.isArray(dados)) {
      return [];
    }

    return dados.map((cliente) => {
      const cnpjLimpo = cliente.cnpj?.replace(/\D/g, "") || "";
      
      const abrirExtrato = () => {
        if (cnpjLimpo) {
          navigate(`/conta-corrente/extrato/${cnpjLimpo}`);
        } else {
          console.error("CNPJ não encontrado para navegação");
        }
      };

      return {
        razaoSocial: (
          <Tooltip title={cliente.razao_social || ""} arrow placement="top">
            <span
              style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#0d2847",
                fontWeight: 500,
                fontSize: "15px",
              }}
            >
              {cliente.razao_social || "-"}
            </span>
          </Tooltip>
        ),
        cnpj: (
          <span style={{ fontFamily: "'Courier New', monospace", color: "#0d2847", fontSize: "15px" }}>
            {formatValue("cnpj", cliente.cnpj || "")}
          </span>
        ),
        saldo: (
          <span
            style={{
              color:
                cliente.saldo_total > 0
                  ? "#2e7d32"
                  : cliente.saldo_total === 0
                  ? "#757575"
                  : "#c62828",
              fontWeight: 600,
              fontSize: "15px",
            }}
          >
            {formatCurrency(cliente.saldo_total || 0)}
          </span>
        ),
        ultimaAtualizacao: (
          <span style={{ color: "#0d2847", fontSize: "15px" }}>
            {formatValue("data", cliente.ultima_atualizacao || "")}
          </span>
        ),
        status: formatStatus(cliente.status || "inativo"),
        botoes: (
          <AcaoIcone titulo="Visualizar extrato" onClick={abrirExtrato}>
            <VisibilityIcon fontSize="small" />
          </AcaoIcone>
        ),
      };
    });
  }, [navigate]);

  const handleInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarClientesContaCorrente();
      // O interceptor do axios já retorna response.data, então response já é o objeto { success, data }
      if (response && response.success && Array.isArray(response.data)) {
        const dadosOrganizados = handleOrganizaDados(response.data);
        setClientes(dadosOrganizados);
      } else {
        console.error("Resposta da API inválida:", response);
        setClientes([]);
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar dados. Resposta inválida.",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      setClientes([]);
      
      // Se for erro 401, mostrar mensagem específica sem fazer logout
      if (error.response?.status === 401) {
        setObjSnackBar({
          type: "error",
          msg: "Erro de autenticação. Verifique se o token está válido.",
        });
      } else {
        setObjSnackBar({
          type: "error",
          msg: error.response?.data?.mensagem ?? error?.mensagem ?? "Ops, erro ao buscar clientes.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [handleOrganizaDados]);

  useEffect(() => {
    handleInitialData();
  }, [handleInitialData]);

  const handlePesquisaPorTexto = async (texto) => {
    setLoading(true);
    try {
      const response = await listarClientesContaCorrente({ search: texto });
      // O interceptor do axios já retorna response.data, então response já é o objeto { success, data }
      if (response && response.success && Array.isArray(response.data)) {
        const dadosOrganizados = handleOrganizaDados(response.data);
        setClientes(dadosOrganizados);
      }
    } catch (error) {
      console.error(error);
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem ?? error?.mensagem ?? "Ops, erro ao buscar clientes.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDebounce = debounce(handlePesquisaPorTexto, 300);

  return (
    <>
      <TableCard
        iconHeader={<BarChartIcon sx={{ fontSize: 32, color: "#1b3a5f" }} />}
        titleHeader={
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1b3a5f", fontSize: "1.625rem", letterSpacing: "-0.3px" }}>
            Conta Corrente
          </Typography>
        }
        headerSx={{ alignItems: "center", padding: "20px 24px 14px" }}
        hideViewToggle={true}
        stickyActions={true}
        columnWidths={COLUNAS_LARGURA}
        tableMinWidth="980px"
        data={clientes}
        setData={setClientes}
        columns={columns}
        textHeaderCard="razaoSocial"
        handleDebounce={handleDebounce}
        loading={loading}
      />

      <SnackBar
        objSnackBar={objSnackBar}
        setObjSnackBar={setObjSnackBar}
      />
    </>
  );
};

export default ContaCorrente;
